import { Card, CardContent, Divider, Tooltip, useMediaQuery, Box } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { MetricProps, MetricsTransparentBar } from "components/MetricsToolbar";
import TextSelect from "components/TextSelect";
import { TextTab } from "components/TextTab";
import formatHighchartsNumber from "helpers/formatHighchartsNumber";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  selectCountOfCompletedReviews,
  selectCountOfCompletedReviewsTimeline,
  selectCountOfPendingReviews,
  selectAdminTimeWhen,
  setAdminTimeInterval,
  TimeWhen,
  fetchCaseStats,
  selectReviewerCaseStats,
} from "redux/slices/clientLens";
import theme from "theme";
import { P } from "theme/typography";
import CaseStats from "../reviewer/CaseStats";
import CompletedReviews from "./CompletedReviews";
import CompletedReviewsTimeline from "./CompletedReviewsTimeline";
import LensAdminManagement from "./management/Management";
import PendingReviews from "./PendingReviews";
import LensAdminReports from "./LensAdminReports";
import { selectActiveFirmIdN } from "redux/slices/client";
import { useSnackbar } from "notistack";

const options = [
  {
    label: "This Week",
    value: TimeWhen.ThisWeek,
  },
  {
    label: "Last Week",
    value: TimeWhen.LastWeek,
  },
  {
    label: "This Month",
    value: TimeWhen.ThisMonth,
  },
  {
    label: "Last Month",
    value: TimeWhen.LastMonth,
  },
  {
    label: "This Year",
    value: TimeWhen.ThisYear,
  },
  {
    label: "Last Year",
    value: TimeWhen.LastYear,
  },
];

const styles: { [k: string]: SystemStyleObject } = {
  root: {
    height: "100%",
    minHeight: 0,
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "32px 16px",
    gridTemplateAreas: `
      "totalbar totalbar totalbar"
      "pending activecases throughput"
      "reportsmanagement reportsmanagement reportsmanagement"
    `,
    [theme.breakpoints.only("xs")]: {
      gridTemplateColumns: "1fr",
      gridTemplateAreas: `
        "totalbar"
        "pending"
        "activecases"
        "throughput"
        "reportsmanagement"
      `,
    },
  },
  reportsmanagement: {
    height: "100%",
    minHeight: 0,
    gridArea: "reportsmanagement",
    display: "grid",
    gap: "32px 24px",
    gridTemplateColumns: "1fr 1fr",
    [theme.breakpoints.only("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  totalbar: { gridArea: "totalbar" },
  activecases: {
    gridArea: "activecases",
    display: "flex",
    flexDirection: "column",
  },
  throughput: { gridArea: "throughput" },
  pending: {
    gridArea: "pending",
    display: "flex",
    flexDirection: "column",
  },
  activitymanagement: { gridArea: "activitymanagement" },
  title: { gridArea: "title" },
};

interface LensAdminProps {
  lensId: number;
}

const LensAdmin = (props: LensAdminProps) => {
  const { lensId } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [completedChart, setCompletedChart] = useState<"individual" | "team">("team");
  const countOfPendingReviews = useAppSelector(selectCountOfPendingReviews);
  const countOfCompletedReviews = useAppSelector(selectCountOfCompletedReviews);
  const countOfCompletedReviewsTimeline = useAppSelector(selectCountOfCompletedReviewsTimeline);
  const caseStats = useAppSelector(selectReviewerCaseStats);
  const timeframe = useAppSelector(selectAdminTimeWhen);
  const activeFirmId = useAppSelector(selectActiveFirmIdN);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const fetchCaseStatsPromise = dispatch(fetchCaseStats({ lensId, firmId: activeFirmId, assigneeId: undefined }));
    fetchCaseStatsPromise.unwrap().catch((error) => {
      if (error?.name !== "AbortError") {
        console.error(error);
        enqueueSnackbar("Failed to fetch case stats", { variant: "error" });
      }
    });
    return () => {
      fetchCaseStatsPromise.abort();
    };
  }, [lensId, activeFirmId]);

  const updateWhen = (timeWhen: TimeWhen) => {
    dispatch(setAdminTimeInterval({ timeWhen }));
  };
  const metrics: MetricProps[] = [
    {
      title: isMobile ? "Cases" : "Total Cases",
      value: (caseStats.activeCount + caseStats.inactiveCount).toLocaleString() || 0,
    },
    { title: isMobile ? "Active" : "Active Cases", value: caseStats.activeCount.toLocaleString() || 0 },
    { title: isMobile ? "Inactive" : "Inactive Cases", value: caseStats.inactiveCount.toLocaleString() || 0 },
  ];

  return (
    <Box sx={styles.root}>
      <Box sx={styles.totalbar}>
        <MetricsTransparentBar metrics={metrics} />
      </Box>
      <Box sx={styles.activecases}>
        <Card id="lens-admin-active-cases">
          <CardContent sx={{ height: 200, padding: "16px!important" }}>
            <P variant="body3" sx={{ mb: 2 }}>
              Active Cases
            </P>
            <CaseStats height={154} />
          </CardContent>
        </Card>
      </Box>
      <Box sx={styles.pending}>
        <Card id="lens-admin-pending-reviews">
          <CardContent sx={{ maxHeight: 200, height: 200, padding: "16px!important" }}>
            <P variant="body3" sx={{ mb: 2 }}>
              {countOfPendingReviews} Pending Reviews
            </P>
            <PendingReviews height={154} lensId={lensId} />
          </CardContent>
        </Card>
      </Box>
      <Box sx={styles.throughput}>
        <Card sx={{ height: 200, maxHeight: 200 }} id="lens-admin-reviews-completed">
          <CardContent sx={{ padding: "8px!important" }}>
            <Box sx={{ display: "flex" }}>
              <P variant="body3" sx={{ mb: 2 }}>
                <Tooltip
                  title={
                    completedChart === "individual"
                      ? countOfCompletedReviews.toLocaleString()
                      : countOfCompletedReviewsTimeline.toLocaleString()
                  }>
                  <span>
                    {completedChart === "individual"
                      ? formatHighchartsNumber(countOfCompletedReviews).toLocaleString()
                      : formatHighchartsNumber(countOfCompletedReviewsTimeline).toLocaleString()}{" "}
                    Reviews Completed
                  </span>
                </Tooltip>
                <TextSelect value={timeframe} onChange={(event) => updateWhen(event.target.value)} options={options} />
              </P>
              <Box sx={{ flexGrow: 1 }}></Box>
              <Box sx={{ paddingTop: "2px", display: "flex" }}>
                <P variant="subtitle3">
                  <TextTab active={completedChart === "individual"} click={() => setCompletedChart("individual")}>
                    Individual
                  </TextTab>
                </P>
                <Divider sx={{ mx: 1, height: "14px" }} orientation="vertical" />
                <P variant="subtitle3">
                  <TextTab active={completedChart === "team"} click={() => setCompletedChart("team")}>
                    Team
                  </TextTab>
                </P>
              </Box>
            </Box>
            {completedChart === "individual" && <CompletedReviews lensId={lensId} height={154} />}
            {completedChart === "team" && <CompletedReviewsTimeline height={154} lensId={lensId} />}
          </CardContent>
        </Card>
      </Box>
      <Box sx={styles.reportsmanagement}>
        <Box
          id="lens-admin-reports"
          sx={{
            minHeight: 0,
            height: "100%",
            overflow: "auto",
          }}>
          <P variant="h6" sx={{ mb: 2 }}>
            Reports
          </P>

          <LensAdminReports lensId={lensId} />
        </Box>

        <Box>
          <P variant="h6" sx={{ mb: 2 }}>
            Management
          </P>

          <LensAdminManagement lensId={lensId} />
        </Box>
      </Box>
    </Box>
  );
};

export default LensAdmin;
